import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Modal, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { getCookie } from "@utils/utilsCookie";
import { axiosInstance } from "../../services/config";
import { useParams } from "react-router-dom";
import { useAuth } from "@context/auth";
import {
  CancelButtonLabel,
  ContentContainer,
  Divider,
  ErrorMessage,
  ModalContainer,
  RootContainer,
  SuccessMessageDescription,
  SuccessMessageTitle,
  Title,
} from "./style";
import { ServicesURL } from "@utils/utilsApi";

interface UploadSitesModalCstProps {
  isOpen: boolean;
  onClose: () => void;
  onUploaded?: () => void;
}

const sessionId = getCookie("sessionId");
const fileMaxSize = 52428800;

const parseFileNameFromContentDisposition = (cd?: string): string | null => {
  if (!cd) return null;
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd);
  const fileName = decodeURIComponent(match?.[1] || match?.[2] || "");
  return fileName || null;
};

const triggerBrowserDownload = (blob: Blob, fileName: string) => {
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
};

const UploadSitesModalCst = ({
  isOpen,
  onClose,
  onUploaded,
}: UploadSitesModalCstProps) => {
  const { t } = useTranslation(["locations-page"]);
  const theme = useTheme();
  const { id } = useParams();
  const { user } = useAuth();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDragActive, setIsDragActive] = useState(false);

  const corporateId = id;
  const uploadUserId = user?.id_user;

  const templateUrl = corporateId
    ? ServicesURL.getSitesDownloadTemplate.replace(
        ":corporateId",
        `${corporateId}`,
      )
    : null;

  const uploadUrl = corporateId
    ? ServicesURL.postSitesUpload.replace(":corporateId", `${corporateId}`)
    : null;

  const downloadExistingSitesEndpoint = corporateId
    ? ServicesURL.getSitesDownload.replace(":corporateId", `${corporateId}`)
    : null;

  useEffect(() => {
    setShowSuccessMessage(false);
    setSelectedFile(undefined);
    setErrorMessage("");
    setIsDragActive(false);
  }, [isOpen]);

  const onUploadClick = () => {
    if (!isLoading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const validateAndSetFile = (file?: File) => {
    if (!file) return;

    if (
      !["text/csv", "application/csv"].includes(file.type) &&
      !file.name.toLowerCase().endsWith(".csv")
    ) {
      setErrorMessage(
        t("validation.file_type_error", { acceptedFormats: "CSV" })!,
      );
      setSelectedFile(undefined);
      return;
    }

    if (file.size > fileMaxSize) {
      setErrorMessage(
        t("validation.file_size_error", { maxSize: fileMaxSize / 1000000 })!,
      );
      setSelectedFile(undefined);
      return;
    }

    setErrorMessage("");
    setSelectedFile(file);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    validateAndSetFile(file);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isLoading) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);

    if (isLoading) return;

    const file = event.dataTransfer.files?.[0];
    validateAndSetFile(file);
  };

  const onDownloadTemplate = async () => {
    try {
      if (!templateUrl) {
        setErrorMessage("CorporateId mancante");
        return;
      }

      setIsLoading(true);

      const res = await axiosInstance.get(templateUrl, {
        headers: {
          "x-ada-session-token": sessionId,
          accept: "text/csv",
        },
        responseType: "blob",
      });

      const cd = res.headers?.["content-disposition"];
      const fileName =
        parseFileNameFromContentDisposition(cd) ||
        "sites_bulk_upload_template.csv";

      triggerBrowserDownload(res.data, fileName);
    } catch (e) {
      console.error("download template sites error", e);
      setErrorMessage("Errore download template");
    } finally {
      setIsLoading(false);
    }
  };

  const onDownloadExistingSites = async () => {
    try {
      if (!downloadExistingSitesEndpoint) {
        setErrorMessage("CorporateId mancante");
        return;
      }

      setIsLoading(true);

      const res = await axiosInstance.get(downloadExistingSitesEndpoint, {
        headers: {
          "x-ada-session-token": sessionId,
          accept: "text/csv",
        },
        responseType: "blob",
      });

      const cd = res.headers?.["content-disposition"];
      const fileName =
        parseFileNameFromContentDisposition(cd) || "sites_bulk_download.csv";

      triggerBrowserDownload(res.data, fileName);
      setErrorMessage("");
    } catch (e) {
      console.error("download existing sites error", e);
      setErrorMessage("Errore download sedi esistenti");
    } finally {
      setIsLoading(false);
    }
  };

  const onConfirm = async () => {
    try {
      if (!selectedFile) return;

      if (!uploadUrl) {
        setErrorMessage("CorporateId mancante");
        return;
      }

      if (!uploadUserId) {
        setErrorMessage("UserId mancante (query param userId obbligatorio)");
        return;
      }

      setIsLoading(true);

      const formData = new FormData();
      formData.append("file", selectedFile);

      const finalUrl = `${uploadUrl}?userId=${uploadUserId}`;

      const res = await axiosInstance.post(finalUrl, formData, {
        headers: {
          "x-ada-session-token": sessionId,
          accept: "text/csv",
          "Content-Type": "multipart/form-data",
        },
        responseType: "blob",
        validateStatus: () => true,
      });

      if (res.status === 204) {
        setSelectedFile(undefined);
        setShowSuccessMessage(true);
        setErrorMessage("");
        return;
      }

      if (res.status >= 200 && res.status < 300) {
        setErrorMessage(t("upload_error")!);

        const cd = res.headers?.["content-disposition"];
        const fileName =
          parseFileNameFromContentDisposition(cd) ||
          "sites_bulk_upload_result.csv";

        triggerBrowserDownload(res.data, fileName);
        return;
      }

      setErrorMessage(`Errore upload (status ${res.status})`);
    } catch (error) {
      console.error("fileUpload sites", error);
      setErrorMessage("modal-errors.content");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (showSuccessMessage) {
      onUploaded?.();
    }
    onClose();
  };

  const SuccessMessage = (
    <Stack direction={"column"} gap={5}>
      <SuccessMessageTitle>
        {t("upload-location-modal.success-title")}
      </SuccessMessageTitle>
      <Stack
        direction="row"
        spacing={3}
        alignItems={"center"}
        justifyContent={"center"}
      >
        <CheckIcon color={"success"} />
        <SuccessMessageDescription>
          {t("upload-location-modal.success-message")}
        </SuccessMessageDescription>
      </Stack>
      <Button
        variant={"contained"}
        onClick={handleClose}
        sx={{ alignSelf: "center" }}
      >
        {t("upload-location-modal.close-cta")}
      </Button>
    </Stack>
  );

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <RootContainer>
        <ModalContainer theme={theme}>
          {showSuccessMessage ? (
            SuccessMessage
          ) : (
            <>
              <Stack direction="row" justifyContent="space-between">
                <Title variant="h4">{t("upload-location-modal.title")}</Title>

                <Button
                  onClick={handleClose}
                  sx={{
                    minWidth: 0,
                    p: 0,
                    color: theme.palette.primary.main,
                  }}
                >
                  <CloseIcon fontSize="large" />
                </Button>
              </Stack>

              <Divider />

              <ContentContainer
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 4,
                  alignItems: "stretch",
                }}
              >
                <input
                  type="file"
                  accept=".csv,.CSV"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />

                <Stack
                  spacing={2}
                  sx={{
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    endIcon={<FileDownloadIcon />}
                    variant="outlined"
                    onClick={onDownloadExistingSites}
                    disabled={isLoading}
                    sx={{
                      width: "100%",
                      height: "48px",
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      fontWeight: 700,
                      fontSize: "18px",
                      textTransform: "none",
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {t("upload-location-modal.download")}
                  </Button>

                  <Button
                    endIcon={<FileDownloadIcon />}
                    variant="contained"
                    onClick={onDownloadTemplate}
                    disabled={isLoading}
                    sx={{
                      width: "100%",
                      height: "48px",
                      fontWeight: 700,
                      fontSize: "18px",
                      textTransform: "none",
                    }}
                  >
                    {t("upload-location-modal.download-template-cta")}
                  </Button>
                </Stack>

                <Box
                  onClick={onUploadClick}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  sx={{
                    border: `2px dashed ${theme.palette.primary.main}`,
                    borderRadius: "4px",
                    minHeight: "150px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    px: 3,
                    cursor: isLoading ? "not-allowed" : "pointer",
                    backgroundColor: isDragActive
                      ? "rgba(244, 62, 99, 0.08)"
                      : "transparent",
                    transition: "all 0.2s ease",
                    opacity: isLoading ? 0.7 : 1,
                  }}
                >
                  <FileUploadIcon
                    sx={{
                      color: theme.palette.primary.main,
                      fontSize: 34,
                      mb: 1,
                    }}
                  />

                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "18px",
                      color: theme.palette.text.primary,
                      mb: 1,
                    }}
                  >
                    {selectedFile
                      ? selectedFile.name
                      : t("upload-location-modal.upload-cta")}
                  </Typography>

                  {!selectedFile && (
                    <Typography
                      sx={{
                        textAlign: "center",
                        color: theme.palette.text.primary,
                        fontSize: "14px",
                        wordBreak: "break-word",
                        maxWidth: "320px",
                        lineHeight: 1.4,
                      }}
                    >
                      {t("upload-location-modal.dragDrop")}
                    </Typography>
                  )}

                  {selectedFile && (
                    <Typography
                      sx={{
                        mt: 1,
                        textAlign: "center",
                        color: theme.palette.text.primary,
                        fontSize: "14px",
                        wordBreak: "break-word",
                      }}
                    >
                      File selezionato: {selectedFile.name}
                    </Typography>
                  )}
                </Box>
              </ContentContainer>

              {errorMessage && (
                <ErrorMessage
                  dangerouslySetInnerHTML={{ __html: errorMessage }}
                />
              )}

              <Divider />

              <Stack direction={"row"} spacing={2}>
                <Button variant={"text"} onClick={handleClose}>
                  <CancelButtonLabel>
                    {t("upload-location-modal.cancel-cta")}
                  </CancelButtonLabel>
                </Button>
                <Button
                  disabled={!selectedFile || isLoading}
                  variant={"contained"}
                  onClick={onConfirm}
                >
                  {t("upload-location-modal.confirm-cta")}
                </Button>
              </Stack>
            </>
          )}
        </ModalContainer>
      </RootContainer>
    </Modal>
  );
};

export default UploadSitesModalCst;
